import React, { useEffect, useState, useMemo } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MenuLogo from "../../assets/demp_Logo.svg";
import { useDempStore } from "src/context/stores";
import { usePlantsList } from "../../api/userInfo";
import { cn } from "src/lib/utils";
import { DEMP_FEATURES } from "src/shared/config";

const SideBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("main");
  const [isOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [prevParentDetails, setPrevParentDetails] = useState({});

  const [dashboardId, companyId] = useDempStore((state) => [
    state.dashboardId,
    state.companyId,
  ]);
  const { plantsList } = usePlantsList();

  const companyInfo = useMemo(() => {
    const companyDetails = plantsList?.find(
      (company) => company.id === companyId
    ) || { function: [], role: "" };

    if (companyDetails?.id) {
      setPrevParentDetails(companyDetails);
      return companyDetails;
    } else {
      if (prevParentDetails?.id) {
        return prevParentDetails;
      }
      return { function: [], role: "" };
    }
  }, [plantsList, companyId, prevParentDetails]);

  // the condition for check role between vekin and fraser -------------------
  const isFrasers = window?.location?.hostname?.includes("frasers");
  const userRole = companyInfo?.role;

  const functionRole = useMemo(() => {
    //   function for frasers role
    const functionRoleEditorFrasers = ["TK"];
    const functionRoleSiteManageFrasers = ["TK"];
    const functionRoleBuApproverFrasers = ["DP", "MP", "TK"];
    const functionRoleValidatorFrasers = ["DP", "MP", "TK"];
    const functionRoleCompanyAdminFrasers = ["DP", "MP"];

    //   function for vekin role
    const functionRoleOwnerVekin = companyInfo?.function;
    const functionRolePlantAdminVekin = companyInfo?.function;
    const functionRoleUserVekin = companyInfo?.function?.filter(
      (f) => f !== "ST"
    );
    const functionRoleBankVekin = companyInfo?.function;
    const functionRoleResellerVekin = companyInfo?.function;
    const functionRoleCooperateVekin = companyInfo?.function;

    return isFrasers
      ? userRole === "Editor"
        ? functionRoleEditorFrasers
        : userRole === "Site Manager"
        ? functionRoleSiteManageFrasers
        : userRole === "BU Approver"
        ? functionRoleBuApproverFrasers
        : userRole === "Validator"
        ? functionRoleValidatorFrasers
        : userRole === "Company Admin"
        ? functionRoleCompanyAdminFrasers
        : companyInfo?.function
      : // function for vekin role
      userRole === "Owner"
      ? functionRoleOwnerVekin
      : userRole === "Plant Admin"
      ? functionRolePlantAdminVekin
      : userRole === "User"
      ? functionRoleUserVekin
      : userRole === "Bank"
      ? functionRoleBankVekin
      : userRole === "Reseller"
      ? functionRoleResellerVekin
      : userRole === "Cooperate"
      ? functionRoleCooperateVekin
      : companyInfo?.function;
  }, [isFrasers, userRole, companyInfo?.function]);

  // _________________________________________________________________________

  const activeMenu = location.pathname;

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const menuList = [
    {
      title: t("side_bar.dashboard"),
      icon: <i className="text-lg ri-dashboard-line"></i>,
      path: `/plant/dashboard/${dashboardId}`,
      visible: functionRole.includes(DEMP_FEATURES.DEMP),
    },
    {
      title: t("side_bar.dap"),
      icon: <i className="text-lg ri-pie-chart-line"></i>,
      path: "/dap-service",
      visible: functionRole.includes(DEMP_FEATURES.DAP),
    },
    {
      title: t("side_bar.map"),
      icon: <i className="text-lg ri-map-pin-line"></i>,
      path: "/site",
      visible: functionRole.includes(DEMP_FEATURES.MAP),
    },
    // {
    //   title: "Data Source Management",
    //   icon: <i class="text-lg ri-lightbulb-line"></i>,
    //   path: "/data-source-mng",
    //   visible: true, //userFunction.includes("DSM")
    // },
    // {
    //   title: "Carbon Event",
    //   icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 py-1" viewBox="0 0 24 24" fill="currentColor"><path d="M9 4L6 2L3 4V19C3 20.6569 4.34315 22 6 22H20C21.6569 22 23 20.6569 23 19V16H21V4L18 2L15 4L12 2L9 4ZM19 16H7V19C7 19.5523 6.55228 20 6 20C5.44772 20 5 19.5523 5 19V5.07037L6 4.4037L9 6.4037L12 4.4037L15 6.4037L18 4.4037L19 5.07037V16ZM20 20H8.82929C8.93985 19.6872 9 19.3506 9 19V18H21V19C21 19.5523 20.5523 20 20 20Z"></path></svg>,
    //   path: "/carbon-event",
    //   visible: userFunction.includes(DEMP_FEATURES.EVENT)
    // },
    {
      title: t("side_bar.data_source_management"),
      icon: <i className="text-lg ri-lightbulb-line"></i>,
      path: "/data-source-mng",
      visible: functionRole.includes(DEMP_FEATURES.DATA_SOURCE),
    },
    {
      title: t("side_bar.carbon_event"),
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 py-1"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M9 4L6 2L3 4V19C3 20.6569 4.34315 22 6 22H20C21.6569 22 23 20.6569 23 19V16H21V4L18 2L15 4L12 2L9 4ZM19 16H7V19C7 19.5523 6.55228 20 6 20C5.44772 20 5 19.5523 5 19V5.07037L6 4.4037L9 6.4037L12 4.4037L15 6.4037L18 4.4037L19 5.07037V16ZM20 20H8.82929C8.93985 19.6872 9 19.3506 9 19V18H21V19C21 19.5523 20.5523 20 20 20Z"></path>
        </svg>
      ),
      path: "/carbon-event",
      visible: functionRole.includes(DEMP_FEATURES.EVENT),
    },
    {
      title: t("side_bar.device_management"),
      icon: <i className="text-lg ri-stack-line"></i>,
      path: "/device-management",
      visible: functionRole.includes(DEMP_FEATURES.DEVICE),
    },
    {
      title: t("side_bar.task_management"),
      icon: <i className="text-lg ri-task-line"></i>,
      path: "/task-management",
      visible: functionRole.includes(DEMP_FEATURES.TASK_MANAGEMENT),
    },
    {
      title: t("side_bar.shipment"),
      icon: <i className="text-lg ri-ship-line"></i>,
      path: "/pat",
      visible: functionRole.includes(DEMP_FEATURES.PAT),
    },
    {
      title: t("side_bar.cero_crm"),
      icon: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
        >
          <path
            d="M18.7161 10.9057L17.193 8.21996H13.3019L15.1002 11.3869C15.3172 11.7694 15.3172 12.2348 15.1002 12.6174L12.8252 16.6244C12.7167 16.8137 12.5617 16.9675 12.3834 17.074C12.2013 17.1804 11.9959 17.2396 11.7788 17.2396H7.23279C6.79873 17.2396 6.39954 17.0069 6.18638 16.6244L3.91142 12.6174C3.69439 12.2348 3.69439 11.7694 3.91142 11.3869L6.18638 7.37991C6.18638 7.37991 6.19414 7.36414 6.21351 7.33653C6.42667 6.96581 7.9769 4.35496 8.90704 3.3335H5.82983C5.05859 3.3335 4.34936 3.75155 3.96181 4.42989L0.287761 10.9057C-0.0959205 11.5841 -0.0959205 12.4202 0.287761 13.0985L3.96181 19.5704C4.34549 20.2488 5.05859 20.6668 5.82983 20.6668H13.174C13.9453 20.6668 14.6545 20.2488 15.0421 19.5704L16.3636 17.2396L18.7122 13.0985C19.0959 12.4202 19.0959 11.5841 18.7122 10.9057H18.7161Z"
            fill="url(#gradient)"
          />
          <path
            d="M23.7122 10.9057L20.0382 4.42989C19.6545 3.75155 18.9414 3.3335 18.174 3.3335H10.826C10.0547 3.3335 9.34549 3.75155 8.96181 4.42989L7.63636 6.76072L5.28776 10.9018C4.90408 11.5801 4.90408 12.4162 5.28776 13.0946L6.8845 15.9066H10.7756L8.90755 12.6134C8.69052 12.2309 8.69052 11.7655 8.90755 11.3829L11.1825 7.37597C11.291 7.18666 11.446 7.02891 11.6243 6.92637C11.8065 6.81988 12.0119 6.76072 12.2289 6.76072H16.775C17.2052 6.76072 17.6043 6.99341 17.8214 7.37597L20.0963 11.3829C20.3134 11.7655 20.3134 12.2309 20.0963 12.6134L17.8214 16.6204C17.8214 16.6204 17.8175 16.6283 17.8097 16.6441C17.6508 16.9083 16.0967 19.5152 15.0813 20.6668H18.174C18.9453 20.6668 19.6545 20.2488 20.0382 19.5704L23.7122 13.0985C24.0959 12.4202 24.0959 11.5841 23.7122 10.9057Z"
            fill="url(#paint1_linear_47_430)"
          />
          <defs>
            <linearGradient
              id="gradient"
              x1="5.39964"
              y1="18.8605"
              x2="13.7715"
              y2="5.33276"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#45B97C"></stop>
              <stop offset="0.05" stopColor="#45B984"></stop>
              <stop offset="0.23" stopColor="#46BDA2"></stop>
              <stop offset="0.39" stopColor="#46BFB4"></stop>
              <stop offset="0.51" stopColor="#47C0BB"></stop>
              <stop offset="0.61" stopColor="#3BABA9"></stop>
              <stop offset="0.81" stopColor="#1E777B"></stop>
              <stop offset="1" stopColor="#00424D"></stop>
            </linearGradient>
            <linearGradient
              id="paint1_linear_47_430"
              x1="17.6082"
              y1="4.43384"
              x2="11.6318"
              y2="18.4786"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#45B97C"></stop>
              <stop offset="0.05" stopColor="#45B984"></stop>
              <stop offset="0.23" stopColor="#46BDA2"></stop>
              <stop offset="0.39" stopColor="#46BFB4"></stop>
              <stop offset="0.51" stopColor="#47C0BB"></stop>
              <stop offset="0.61" stopColor="#3BABA9"></stop>
              <stop offset="0.81" stopColor="#1E777B"></stop>
              <stop offset="1" stopColor="#00424D"></stop>
            </linearGradient>
          </defs>
        </svg>
      ),
      path: "/customer-management-system",
      visible: functionRole.includes(DEMP_FEATURES.CMS),
    },
    {
      title: "Setting",
      icon: <i className="text-lg ri-settings-2-line"></i>,
      path: "/setting",
      visible: functionRole.includes(DEMP_FEATURES.SETTING),
    },
  ];

  //   remove pathname from session storage when it is not dap-service page
  useEffect(() => {
    const isDapService = location?.pathname?.includes("dap-service");

    if (!isDapService) {
      sessionStorage.removeItem("pathname");
    }
  }, [location.pathname]);

  // Redirect user depending on their DEMP_FEATURES roles:
  useEffect(() => {
    if (companyInfo?.function?.length === 0) return;

    const pathnameGroup = {
      DEMP: ["plant", "event-transaction", "analytics", "report", "esg"],
      DAP: ["dap-service"],
      MAP: ["site"],
      DATA_SOURCE: ["data-source-mng"],
      EVENT: ["carbon-event"],
      DEVICE: ["device-management"],
      TASK_MANAGEMENT: ["task-management"],
      PAT: ["pat"],
      CMS: ["customer-management-system"],
      SETTING: ["setting"],
    };

    const featureKeys = Object?.keys(DEMP_FEATURES);

    // Find all features this role has access to
    const accessibleFeatures = featureKeys?.filter((key) =>
      functionRole?.includes(DEMP_FEATURES[key])
    );

    const accessiblePath = accessibleFeatures?.map(
      (feature) => pathnameGroup[`${feature}`]
    );

    const getPathname = location?.pathname?.split("/")[2];

    // Redirect to first accessible feature if functionRole isn't match with accessiblePath
    if (
      accessiblePath?.length > 0 &&
      !accessiblePath?.some((path) => path?.includes(getPathname))
    ) {
      navigate(`/${accessiblePath[0]}`);
    }
  }, [functionRole, navigate, location?.pathname, companyInfo?.function]);

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <div
        className={
          "w-16 h-full bg-gradient-to-b from-vekin-tertiary to-vekin-primary text-white flex flex-col transition-all duration-300 shadow-lg" +
          (isMobile ? " min-h-screen" : " h-screen")
        }
      >
        {/* Logo Section */}
        <div className="flex items-center justify-between h-20 px-2">
          <img
            src={MenuLogo}
            alt="Logo"
            className={`h-12 transition-all duration-300 ${
              isOpen ? "scale-100" : "scale-75"
            }`}
          />
        </div>

        {/* Menu Items */}
        <div className="flex flex-col gap-4 px-2 mt-4 flex-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {menuList
            ?.filter((menu) => menu.title !== "Setting")
            ?.map(
              (menu, index) =>
                menu.visible && (
                  <div className="relative group" key={menu.title}>
                    <Link
                      to={activeMenu.includes(menu.path) ? "#" : menu.path}
                      className={cn(
                        "relative flex items-center p-2 rounded-lg cursor-pointer transition-colors hover:bg-vekin-primary-dark/35",
                        {
                          "bg-gradient-to-bl from-vekin-primary-dark/85 to-vekin-primary-dark/10":
                            activeMenu.includes(menu.path),
                        }
                      )}
                    >
                      <span className={"flex items-center justify-center w-8"}>
                        {menu.icon}
                      </span>
                    </Link>
                    {/* Tooltip for item name - shown on hover */}
                    <span className="hidden absolute px-2 py-1 text-xs text-white transition-opacity duration-200 bg-vekin-primary rounded shadow-lg z-[999] left-[3.7rem] group-hover:flex whitespace-nowrap top-3">
                      {menu.title}
                    </span>
                  </div>
                )
            )}
        </div>

        {/* Settings Menu - Centered */}
        {menuList
          ?.filter((menu) => menu.title === "Setting")
          ?.map(
            (menu, index) =>
              menu.visible && (
                <div className="flex justify-center mb-4">
                  <div className="relative group" key={menu.title}>
                    <Link
                      to={activeMenu.includes(menu.path) ? "#" : menu.path}
                      className={cn(
                        "relative flex items-center p-2 rounded-lg cursor-pointer transition-colors hover:bg-vekin-primary-dark/35",
                        {
                          "bg-gradient-to-bl from-vekin-primary-dark/85 to-vekin-primary-dark/10":
                            activeMenu.includes(menu.path),
                        }
                      )}
                    >
                      <span className={"flex items-center justify-center w-8"}>
                        {menu.icon}
                      </span>
                    </Link>
                    {/* Tooltip for item name - shown on hover */}
                    <span className="hidden absolute px-2 py-1 text-xs text-white transition-opacity duration-200 bg-vekin-primary rounded shadow-lg z-[999] left-[3.7rem] group-hover:flex whitespace-nowrap top-3">
                      {menu.title}
                    </span>
                  </div>
                </div>
              )
          )}
      </div>
    </div>
  );
};

export default SideBar;
